import { useEffect, useRef } from "react";
import { DOMAIN } from "@shared/brand";
import { AlertTriangle } from "lucide-react";

/**
 * STAGING-BANNER-1 (s149) — a loud, persistent, non-dismissible bar that makes the STAGING
 * deployment visually unmistakable from production, so the staging/prod two-tab trap (mistaking
 * `estate-planner-production-9cb3.up.railway.app` for `www.organized-estate.com`) can't recur.
 *
 * ENV VAR: driven by `VITE_SENTRY_ENVIRONMENT` — the EXISTING client-side environment signal
 * (production | staging | development; defaults to "development" when absent; also read in
 * `main.tsx` for Sentry). No new config (G7: one env check).
 *
 * VISIBILITY: fail-safe — shows on every environment that is NOT explicitly "production". The only
 * way to hide the banner is to BE production, so a staging box that forgot to set the var still
 * warns rather than masquerading as prod. Prod ("production") shows nothing here; its own
 * FFTestingBanner behaviour is unchanged. Renders logged-out too (env-only, no auth dependency)
 * and is not dismissible (unlike the F&F banner).
 *
 * Distinct on purpose: a loud orange bar, unlike the F&F banner's soft amber (#FBF3E2).
 */
/**
 * s184 — this banner is `sticky top-0`, so it stays PINNED to the viewport top at every scroll
 * position. The desktop sidebar (`ui/sidebar.tsx`) is `position: fixed` and anchors to the viewport,
 * not to document flow, so without help it slides underneath this bar and its brand area is clipped.
 *
 * We publish the banner's LIVE height as `--staging-banner-height` on the document element; the
 * sidebar offsets its top by it. Measured rather than hardcoded because the inner row is
 * `flex-wrap` — at narrow widths the copy wraps to two lines and any constant would be wrong.
 * Cleared to `0px` on unmount and never set at all on production, so prod layout is untouched.
 */
function useSharedStagingBannerHeight(active: boolean) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    if (!active) {
      root.style.setProperty("--staging-banner-height", "0px");
      return;
    }
    const el = ref.current;
    if (!el) return;

    const publish = () => {
      root.style.setProperty("--staging-banner-height", `${Math.round(el.getBoundingClientRect().height)}px`);
    };
    publish();

    // ResizeObserver is feature-detected: jsdom does not implement it, and constructing it blind
    // threw out of the effect and took the whole component down under test. `resize` alone still
    // catches the reflow that actually changes this bar's height (the copy wrapping to two lines).
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", publish);
      root.style.setProperty("--staging-banner-height", "0px");
    };
  }, [active]);

  return ref;
}

export function StagingBanner() {
  const env = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? "development";
  const isProduction = env === "production";
  const ref = useSharedStagingBannerHeight(!isProduction);

  if (isProduction) return null;

  return (
    <div
      ref={ref}
      role="alert"
      data-testid="staging-banner"
      className="sticky top-0 z-[100] w-full text-center text-[13px] font-semibold uppercase tracking-wide"
      style={{ backgroundColor: "#EA580C", color: "#FFFFFF", borderBottom: "2px solid #9A3412" }}
    >
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-center gap-x-2 px-4 py-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>STAGING — test site, not {DOMAIN.primary}</span>
      </div>
    </div>
  );
}
